
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Zap, PlusCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";

interface Reading {
  id: string;
  reading_value: number;
  reading_date: string;
  [key: string]: any;
}

interface ElectricityCalculatorProps {
  electricityReadings: Reading[];
  electricityReading1: string;
  setElectricityReading1: (value: string) => void;
  electricityReading2: string;
  setElectricityReading2: (value: string) => void;
  electricityResult: string;
  setElectricityResult?: (value: string) => void;
  selectedHouse?: string;
  selectedMonth?: string;
}

export const ElectricityCalculator = ({
  electricityReadings,
  electricityReading1,
  setElectricityReading1,
  electricityReading2,
  setElectricityReading2,
  electricityResult,
  setElectricityResult = () => {},
  selectedHouse = "",
  selectedMonth = ""
}: ElectricityCalculatorProps) => {
  const [kwhRate, setKwhRate] = useState<string>("");
  const [isCreatingExpense, setIsCreatingExpense] = useState(false);
  const { toast } = useToast();

  // Get selected reading data
  const selectedReading1 = electricityReadings.find(r => r.id === electricityReading1);
  const selectedReading2 = electricityReadings.find(r => r.id === electricityReading2);

  // State to store the parsed result values
  const [parsedCost, setParsedCost] = useState<number | null>(null);
  const [consumptionValue, setConsumptionValue] = useState<string>("");

  // Calculate result based on selected readings
  const calculateResult = () => {
    // Find the selected readings
    const reading1 = electricityReadings.find(r => r.id === electricityReading1);
    const reading2 = electricityReadings.find(r => r.id === electricityReading2);
    
    // Check if all required inputs are provided
    if (!reading1 || !reading2 || !kwhRate || kwhRate === '') {
      setElectricityResult("Faltan valores");
      setParsedCost(null);
      setConsumptionValue("");
      return;
    }
    
    // Get reading values
    const value1 = parseFloat(reading1.reading_value as any);
    const value2 = parseFloat(reading2.reading_value as any);
    
    if (isNaN(value1) || isNaN(value2)) {
      setElectricityResult("Error: Valores de lectura inválidos");
      setParsedCost(null);
      setConsumptionValue("");
      return;
    }
    
    // Get reading dates and convert to Date objects
    const date1 = new Date(reading1.reading_date);
    const date2 = new Date(reading2.reading_date);
    
    // Calculate the difference in days between dates
    const daysDiff = Math.abs((date2.getTime() - date1.getTime()) / (1000 * 3600 * 24));
    
    if (daysDiff === 0) {
      setElectricityResult("Error: Las lecturas son del mismo día");
      setParsedCost(null);
      setConsumptionValue("");
      return;
    }
    
    // Calculate using the formula: (value2 - value1) / daysDiff * 30
    const consumptionPerMonth = (value2 - value1) / daysDiff * 30;
    
    // Format the results with $ amounts
    const kwhRateValue = parseFloat(kwhRate);
    if (isNaN(kwhRateValue)) {
      setElectricityResult("Error: El valor kwh no es válido");
      setParsedCost(null);
      setConsumptionValue("");
      return;
    }
    
    const monthlyCost = consumptionPerMonth * kwhRateValue;
    setParsedCost(monthlyCost);
    
    const formattedConsumption = new Intl.NumberFormat('es-CL', { 
      maximumFractionDigits: 2 
    }).format(consumptionPerMonth);
    
    setConsumptionValue(formattedConsumption);
    
    const formattedCost = new Intl.NumberFormat('es-CL', { 
      style: 'currency', 
      currency: 'CLP'
    }).format(monthlyCost);
    
    const result = `Consumo mensual: ${formattedConsumption} kWh\nCosto estimado: ${formattedCost}`;
    setElectricityResult(result);
  };

  // Add debugging for better visibility into what's happening
  useEffect(() => {
    calculateResult();
  }, [electricityReading1, electricityReading2, kwhRate]);

  const formatReadingDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  // Create an expense from the calculation
  const handleCreateExpense = async () => {
    if (!selectedHouse || !selectedMonth || !parsedCost || parsedCost <= 0) {
      toast({
        title: "Error",
        description: "Faltan datos para crear el gasto. Asegúrese de que haya seleccionado una casa y el cálculo sea válido.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsCreatingExpense(true);
      
      // Get the last day of the selected month for expense_date
      const [year, month] = selectedMonth.split("-").map(Number);
      const lastDay = new Date(year, month, 0).getDate();
      const expenseDate = `${selectedMonth}-${lastDay.toString().padStart(2, '0')}`;

      // Fetch electricity category ID
      const { data: categories, error: categoryError } = await supabase
        .from("expense_categories")
        .select("id")
        .eq("name", "Electricidad")
        .limit(1);

      if (categoryError || !categories || categories.length === 0) {
        throw new Error("No se pudo encontrar la categoría Electricidad");
      }

      const categoryId = categories[0].id;
      
      // Insert the expense
      const { error } = await supabase.from("expenses").insert({
        entity_id: selectedHouse,
        entity_type: 'house',
        amount: Math.round(parsedCost),
        category: categoryId,
        description: `Consumo eléctrico: ${consumptionValue} kWh`,
        expense_date: expenseDate,
      });

      if (error) throw error;

      toast({
        title: "Éxito",
        description: "Gasto de electricidad creado correctamente",
      });
    } catch (error) {
      console.error("Error creating expense:", error);
      toast({
        title: "Error",
        description: "No se pudo crear el gasto de electricidad",
        variant: "destructive",
      });
    } finally {
      setIsCreatingExpense(false);
    }
  };

  // Check if the calculation result is valid for creating an expense
  const hasValidResult = 
    electricityResult && 
    electricityResult.includes("Costo estimado:") && 
    parsedCost !== null && 
    parsedCost > 0;

  return (
    <Card className="shadow-md">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg font-medium">
          <Zap className="h-5 w-5 text-blue-500" />
          Categoría Electricidad
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Primera Lectura</Label>
            <Select value={electricityReading1} onValueChange={setElectricityReading1}>
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="Seleccione lectura" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {electricityReadings.map((reading) => (
                  <SelectItem key={reading.id} value={reading.id}>
                    {`${reading.reading_value} - ${new Date(reading.reading_date).toLocaleDateString()}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedReading1 && (
              <div className="text-sm text-gray-600 mt-1 ml-1">
                <p>Valor: {selectedReading1.reading_value} kWh</p>
                <p>Fecha: {formatReadingDate(selectedReading1.reading_date)}</p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>Segunda Lectura</Label>
            <Select value={electricityReading2} onValueChange={setElectricityReading2}>
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="Seleccione lectura" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {electricityReadings.map((reading) => (
                  <SelectItem key={reading.id} value={reading.id}>
                    {`${reading.reading_value} - ${new Date(reading.reading_date).toLocaleDateString()}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedReading2 && (
              <div className="text-sm text-gray-600 mt-1 ml-1">
                <p>Valor: {selectedReading2.reading_value} kWh</p>
                <p>Fecha: {formatReadingDate(selectedReading2.reading_date)}</p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="electricity-rate">Valor kwh del mes</Label>
            <Input
              id="electricity-rate"
              type="number"
              placeholder="Ingrese valor del kwh"
              className="bg-white"
              value={kwhRate}
              onChange={(e) => setKwhRate(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="electricity-result">Resultado</Label>
            <div id="electricity-result" className="p-3 rounded-md border bg-gray-50 text-gray-700 whitespace-pre-line">
              {electricityResult || "Resultado del cálculo"}
            </div>
          </div>

          <Button 
            onClick={handleCreateExpense} 
            disabled={!hasValidResult || !selectedHouse || isCreatingExpense}
            className="w-full"
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            Crear gasto de electricidad
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
